import {
  BackLink,
  EmptyState,
  ErrorState,
  LoadingState,
  WithHalfdot,
} from "@elyxeer/ui";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { NewsCard } from "~/components/news/NewsCard";
import { Screen } from "~/components/ui/Screen";
import { useLocale } from "~/i18n";
import { fetchSaved } from "~/lib/api";
import { queryKeys } from "~/lib/query-client";
import { useAuthStore } from "~/store/auth-store";

const PAGE_SIZE = 20;

export const SAVED_PATH = "/saved";

/**
 * Uloženo pro mě — everything the member hearted, most recently saved first.
 *
 * The same tiles as the library, so an article looks the same wherever it is found,
 * and the heart on each tile is how it leaves the list.
 */
export function SavedRoute() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const userId = useAuthStore((state) => state.user?.id ?? "");

  const query = useInfiniteQuery({
    queryKey: queryKeys.savedList(userId, locale),
    queryFn: ({ pageParam, signal }) =>
      fetchSaved({ locale, limit: PAGE_SIZE, offset: pageParam, signal }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce((sum, page) => sum + page.items.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    enabled: userId.length > 0,
  });

  const articles = query.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <Screen maxWidth="lg">
      <Box
        data-testid="saved.page"
        sx={{ display: "flex", flexDirection: "column", gap: { xs: 3, md: 4 } }}
      >
        <Box>
          <BackLink
            to="/profile"
            label={t("saved.backToProfile")}
            testId="saved.back"
          />
          <Typography variant="H1" sx={{ color: "text.primary", marginTop: 1 }}>
            <WithHalfdot size={11}>{t("saved.title")}</WithHalfdot>
          </Typography>
          <Typography
            variant="T2"
            sx={{ color: "text.secondary", marginTop: 1 }}
          >
            {t("saved.subtitle")}
          </Typography>
        </Box>

        {query.isPending ? <LoadingState /> : null}

        {query.isError ? (
          <ErrorState
            message={t("saved.error")}
            onRetry={() => void query.refetch()}
          />
        ) : null}

        {query.isSuccess && articles.length === 0 ? (
          <EmptyState message={t("saved.empty")} />
        ) : null}

        {articles.length > 0 ? (
          <Box
            data-testid="saved.list"
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                sm: "repeat(2, minmax(0, 1fr))",
                lg: "repeat(3, minmax(0, 1fr))",
              },
              gap: { xs: 2, md: 3 },
            }}
          >
            {articles.map((article) => (
              <NewsCard key={article.id} article={article} showTopics />
            ))}
          </Box>
        ) : null}

        {query.hasNextPage ? (
          <Button
            variant="outlined"
            onClick={() => void query.fetchNextPage()}
            disabled={query.isFetchingNextPage}
            data-testid="saved.load-more"
            sx={{ alignSelf: "center", minWidth: 240 }}
          >
            {t("saved.loadMore")}
          </Button>
        ) : null}
      </Box>
    </Screen>
  );
}
